import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Address from './Address'
import { Button, Alert } from 'react-bootstrap'
import { ButtonWithLoadingState } from '../../Buttons'
import useMountEffect from '../../../utils/hooks/useMountEffect'
import { emptyGuid } from '../../../utils/emptyGuid'
import apiClient from '../../../utils/api/apiClient'
import { APIEndpoints } from '../../../utils/api/apiConfig'
import { setPointerErrorsFromApiResponse } from '../../../utils/api/apiUtils'
import { cloneDeep } from 'lodash'
import { getMetaProperty } from '../../../utils/customerPrototypes'

const addressTemplate = {
  id: emptyGuid,
  type: null,
  addressType: null,
  isDefaultAddress: false,
  formattedCompleteAddress: '',
  displayName: '',
  secondaryOfficeName: '',
  useSecondaryOfficeName: false,
  addressnumber: '',
  careOf: '',
  postOfficeBox: '',
  street: '',
  streetNr: '',
  city: '',
  zipCode: '',
  countryIsoCode: '',
}

// 🔥 ensures the backend does NOT break with nulls
const ensureBackendShape = (addr) => ({
  ...addr,
  type: addr.type ?? {
    displayName: '',
    type: 'undefined',
    isDefaultType: false,
    isMultipleSelectable: false,
    id: emptyGuid,
  },
  addressType: addr.addressType ?? 'undefined',
  isDefaultAddress: addr.isDefaultAddress ?? false,
  displayName: addr.displayName ?? '',
  formattedCompleteAddress: addr.formattedCompleteAddress ?? '',
  addressnumber: addr.addressnumber ?? '',
  postOfficeBox: addr.postOfficeBox ?? '',
})

// 🔥 always prepare address before sending to backend
const prepareAddress = (addr) => {
  if (!addr) return null

  const a = cloneDeep(addr)

  // convert empty strings to null
  ;['street', 'streetNr', 'zipCode', 'city', 'countryIsoCode'].forEach((f) => {
    if (!a[f] || a[f].trim() === '') a[f] = null
  })

  return ensureBackendShape(a)
}

const AssetGroupAddressesForm = ({ groupDetails }) => {
  const { t } = useTranslation()
  const [groupAddresses, setGroupAddresses] = useState(null)
  const [officeName, setOfficeName] = useState(null)
  const [initialGroupAddresses, setInitialGroupAddresses] = useState(null)

  const [availableCountries, setAvailableCountries] = useState([])
  const [shipmentAddress, setShipmentAddress] = useState(addressTemplate)
  const [billingAddress, setBillingAddress] = useState(addressTemplate)

  const [formErrors, setFormErrors] = useState({})
  const [submitIsLoading, setSubmitIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(false)

  const mountedRef = useRef(true)

  useMountEffect(() => {
    getCountries()
    if (groupDetails) {
      getGroupAddresses()
      setOfficeName(groupDetails.office?.displayName ?? '')
    }
  })

  const getGroupAddresses = () => {
    apiClient
      .getJson(APIEndpoints.assetGroupAdmin(groupDetails.id).addresses)
      .then((response) => {
        if (!mountedRef.current) return

        setInitialGroupAddresses(response)
        initGroupAddresses(response)
      })
      .catch(console.error)
  }

  const initGroupAddresses = (data) => {
    setGroupAddresses(data)
    setShipmentAddress(data.shipmentAddress ?? addressTemplate)
    setBillingAddress(data.billingAddress ?? addressTemplate)
  }

  const getCountries = () => {
    apiClient
      .getJson(APIEndpoints.countries)
      .then((response) => mountedRef.current && setAvailableCountries(response))
      .catch(console.error)
  }

  useEffect(() => {
    return () => {
      mountedRef.current = false
    }
  }, [])

  const resetFormErrorsByKey = useCallback((key) => {
    setFormErrors((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const handleChangeShipmentAddress = (a) => {
    setShipmentAddress(a)
    setGroupAddresses((prev) => ({ ...prev, shipmentAddress: a }))
  }

  const handleChangeBillingAddress = (a) => {
    setBillingAddress(a)
    setGroupAddresses((prev) => ({ ...prev, billingAddress: a }))
  }

  const handleDeleteShipmentAddress = () => {
    const clone = cloneDeep(groupAddresses)
    clone.deleteShipmentAddress = true
    submitAddresses(clone)
  }

  const handleDeleteBillingAddress = () => {
    const clone = cloneDeep(groupAddresses)
    clone.deleteBillingAddress = true
    submitAddresses(clone)
  }

  const validateAddresses = (body) => {
    const errors = {}

    const check = (prefix, addr) => {
      if (!addr.street?.trim()) errors[`${prefix}.street`] = 'streetRequired'
      if (!addr.zipCode?.trim()) errors[`${prefix}.zipCode`] = 'zipRequired'
      if (!addr.city?.trim()) errors[`${prefix}.city`] = 'cityRequired'
      if (!addr.countryIsoCode?.trim())
        errors[`${prefix}.countryIsoCode`] = 'countryRequired'
    }

    check('shipmentAddress', body.shipmentAddress)
    check('billingAddress', body.billingAddress)

    return errors
  }

  const backendMessageMap = {
    streetRequired: t('validation:streetRequired'),
    zipRequired: t('validation:zipRequired'),
    cityRequired: t('validation:cityRequired'),
    countryRequired: t('validation:countryRequired'),
  }

  const submitAddresses = (address) => {
    setSubmitIsLoading(true)
    setError(false)

    const body = cloneDeep(address)

    body.shipmentAddress = prepareAddress(body.shipmentAddress)
    body.billingAddress = prepareAddress(body.billingAddress)

    const clientErrs = validateAddresses(body)
    if (Object.keys(clientErrs).length > 0) {
      const translated = {}
      Object.keys(clientErrs).forEach((key) => {
        const code = clientErrs[key]
        translated[key] = backendMessageMap[code] || code
      })

      setFormErrors(translated)
      setError('validationError')

      setSubmitIsLoading(false)
      return
    }

    apiClient
      .patchJson(
        APIEndpoints.assetGroupAdmin(groupDetails.id).addresses,
        body,
        setSuccess(false)
      )
      .then(() => {
        if (!mountedRef.current) return
        getGroupAddresses()
        setSuccess(true)
      })
      .catch((err) => {
        const pointer = setPointerErrorsFromApiResponse(err)

        Object.keys(pointer).forEach((k) => {
          const backendCode = pointer[k]
          pointer[k] = backendMessageMap[backendCode] || backendCode
        })

        if (Object.keys(pointer).length > 0) {
          setFormErrors(pointer)
          setError('validationError')
        } else {
          setError(true)
        }
      })
      .finally(() => mountedRef.current && setSubmitIsLoading(false))
  }

  const cancel = () => {
    initGroupAddresses(cloneDeep(initialGroupAddresses))
    setSuccess(false)
    setError(false)
    setFormErrors({})
  }

  const dataHasChanged = () =>
    JSON.stringify(groupAddresses) !== JSON.stringify(initialGroupAddresses)

  const isDeletable = (name) =>
    !!getMetaProperty(groupAddresses.metaProperties, `/${name}`, 'isDeletable')

  if (!groupAddresses) return null

  return (
    <>
      <div>
        <h2>{t('assetGroups:shipmentadress')}</h2>
        <Address
          addressType='shipmentAddress'
          address={shipmentAddress}
          officeName={officeName}
          handleChangeAddress={handleChangeShipmentAddress}
          handleDeleteAddress={handleDeleteShipmentAddress}
          availableCountries={availableCountries}
          formErrors={formErrors}
          resetFormErrorsByKey={resetFormErrorsByKey}
          isDeletable={isDeletable('shipmentAddress')}
          backendMessageMap={backendMessageMap}
        />
      </div>

      <div>
        <h2>{t('assetGroups:billingaddress')}</h2>
        <Address
          addressType='billingAddress'
          address={billingAddress}
          officeName={officeName}
          handleChangeAddress={handleChangeBillingAddress}
          handleDeleteAddress={handleDeleteBillingAddress}
          availableCountries={availableCountries}
          formErrors={formErrors}
          resetFormErrorsByKey={resetFormErrorsByKey}
          isDeletable={isDeletable('billingAddress')}
          backendMessageMap={backendMessageMap}
        />
      </div>

      <div>
        <ButtonWithLoadingState
          className='my-2 me-2'
          onClick={() => submitAddresses(groupAddresses)}
          isLoading={submitIsLoading}
          buttonText={t('saveButton')}
          disabled={!dataHasChanged()}
        />

        {dataHasChanged() && (
          <Button className='my-2 me-2' variant='secondary' onClick={cancel}>
            {t('cancelButton')}
          </Button>
        )}
      </div>

      {success && (
        <Alert variant='success'>{t('registrationBox:savingSuccess')}</Alert>
      )}

      {dataHasChanged() && !success && (
        <Alert variant='info'>{t('EmployeeFormHealthData:dataChange')}</Alert>
      )}

      {error && (
        <Alert variant='danger'>
          {t('articleAdminOverlayProgress:danger')}
        </Alert>
      )}
    </>
  )
}

export default AssetGroupAddressesForm
